import { useCallback, useEffect, useMemo, useState } from 'react'
import { arrayMove } from '@dnd-kit/sortable'

import { ApiError } from '../../lib/api'

const idsMatch = (firstId, secondId) => String(firstId) === String(secondId)
const defaultGetId = (item) => item.id

export const getNextOrder = (orderedIds, activeId, overId) => {
  if (activeId == null || overId == null || idsMatch(activeId, overId)) {
    return null
  }

  const oldIndex = orderedIds.findIndex((id) => idsMatch(id, activeId))
  const targetIndex = orderedIds.findIndex((id) => idsMatch(id, overId))
  if (oldIndex === -1 || targetIndex === -1) return null

  return {
    movedId: orderedIds[oldIndex],
    nextOrder: arrayMove(orderedIds, oldIndex, targetIndex),
    targetIndex,
    targetPosition: targetIndex + 1,
  }
}

const useSortableOrder = ({
  items = [],
  getId = defaultGetId,
  updatePosition,
  reload,
  onError,
  disabled = false,
}) => {
  const serverIds = useMemo(() => items.map(getId), [items, getId])
  const serverIdsKey = JSON.stringify(serverIds.map(String))
  const [orderedIds, setOrderedIds] = useState(serverIds)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!disabled) setOrderedIds(serverIds)
    // The serialized key changes only when the server-provided order changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverIdsKey])

  const onDragEnd = useCallback(
    async ({ active, over }) => {
      if (disabled || isSaving || !over) return

      const reorder = getNextOrder(orderedIds, active.id, over.id)
      if (!reorder) return

      const { movedId, nextOrder, targetPosition } = reorder
      const previousOrder = orderedIds
      setOrderedIds(nextOrder)
      setIsSaving(true)

      try {
        const result = await updatePosition(movedId, targetPosition)
        if (result instanceof ApiError) throw result
        reload()
      } catch (error) {
        setOrderedIds(previousOrder)
        onError(error)
      } finally {
        setIsSaving(false)
      }
    },
    [disabled, isSaving, onError, orderedIds, reload, updatePosition]
  )

  return { isSaving, onDragEnd, orderedIds }
}

export default useSortableOrder
